import React, { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { clarityService } from '../services/clarityService'
import { hubspotService } from '../services/hubspotService'
import localApi from '../services/localApi'
import { COOKIE_CONSENT_STORAGE_KEY } from '../services/storageConsent'

interface CookieConsentContextData {
  showBanner: boolean
  acceptCookies: () => Promise<void>
  declineCookies: () => Promise<void>
}

const CookieConsentContext = createContext<CookieConsentContextData>({
  showBanner: false,
  acceptCookies: async () => {},
  declineCookies: async () => {}
})

const STORAGE_KEY = COOKIE_CONSENT_STORAGE_KEY

export const CookieConsentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [showBanner, setShowBanner] = useState(false)

  const acceptCookies = useCallback(async () => {
    const consentData = {
      analytics: true,
      ads: false,
      timestamp: new Date().toISOString()
    }

    localStorage.setItem(STORAGE_KEY, JSON.stringify(consentData))

    clarityService.init()
    clarityService.setConsent(true, false)
    hubspotService.grantConsent()

    try {
      const user = localStorage.getItem('user')
      let userIdentifier = null

      if (user) {
        const userJSON = JSON.parse(user)
        userIdentifier = userJSON.id || userJSON.email || null

        if (userIdentifier) {
          clarityService.identify(String(userIdentifier), userJSON.name)
        }

        if (userJSON.email) {
          hubspotService.identify(String(userJSON.email), userJSON.name ? { name: String(userJSON.name) } : undefined)
        }
      }

      await localApi.post('/cookie-consent/accept', {
        user_identifier: userIdentifier,
        consent_analytics: true,
        consent_ads: false
      })
    } catch (error) {
      console.error('Failed to store cookie consent:', error)
    }

    setShowBanner(false)
  }, [])

  const declineCookies = useCallback(async () => {
    const consentData = {
      analytics: false,
      ads: false,
      timestamp: new Date().toISOString()
    }

    localStorage.setItem(STORAGE_KEY, JSON.stringify(consentData))

    hubspotService.revokeConsent()

    try {
      const user = localStorage.getItem('user')
      let userIdentifier = null

      if (user) {
        const userJSON = JSON.parse(user)
        userIdentifier = userJSON.id || userJSON.email || null
      }

      await localApi.post('/cookie-consent/accept', {
        user_identifier: userIdentifier,
        consent_analytics: false,
        consent_ads: false
      })
    } catch (error) {
      console.error('Failed to store cookie consent:', error)
    }

    setShowBanner(false)
  }, [])

  useEffect(() => {
    const consent = localStorage.getItem(STORAGE_KEY)

    if (consent) {
      const consentData = JSON.parse(consent)

      if (consentData.analytics) {
        clarityService.init()
        clarityService.setConsent(true, false)
        hubspotService.grantConsent()
      }

      setShowBanner(false)
    } else {
      setShowBanner(true)
    }
  }, [])

  useEffect(() => {
    if (showBanner) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }

    return () => {
      document.body.style.overflow = ''
    }
  }, [showBanner])

  return (
    <CookieConsentContext.Provider value={{ showBanner, acceptCookies, declineCookies }}>
      {children}
    </CookieConsentContext.Provider>
  )
}

export const useCookieConsent = (): CookieConsentContextData => {
  const context = useContext(CookieConsentContext)

  if (!context) {
    throw new Error('useCookieConsent must be used within CookieConsentProvider')
  }

  return context
}
